import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ExternalLink, Search, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResearchTable = ({ heading = true }) => {
  const [papers, setPapers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [activeTopic, setActiveTopic] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get(`${API}/research-papers`),
          axios.get(`${API}/research-topics`),
        ]);
        if (!mounted) return;
        setPapers(pRes.data);
        setTopics(tRes.data);
      } catch (e) {
        if (!mounted) return;
        setError("Unable to load research papers.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return papers.filter((p) => {
      const matchTopic =
        !activeTopic || p.topics.some((t) => t === activeTopic);
      const q = query.trim().toLowerCase();
      const matchQuery =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.journal.toLowerCase().includes(q) ||
        p.summary.toLowerCase().includes(q) ||
        (p.year || "").toLowerCase().includes(q) ||
        p.topics.some((t) => t.toLowerCase().includes(q));
      return matchTopic && matchQuery;
    });
  }, [papers, activeTopic, query]);

  return (
    <section
      data-testid="research-hub"
      className="bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        {heading && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
                Academic & Research Hub
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A2540]">
                Peer-reviewed
                <br />
                <span className="italic text-[#2E5AA5]">contributions</span> to
                pathology.
              </h1>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <p className="font-sans text-base text-[#475569] leading-relaxed">
                A curated index of Dr. Jain&rsquo;s published research. Filter
                by topic or search keywords — expand any entry to read the
                clinical-impact summary and open the paper.
              </p>
            </div>
          </div>
        )}

        <div className="border-t border-b border-[#E2E8F0] py-6 mb-10 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="relative flex-1 max-w-xl">
            <Search
              size={16}
              strokeWidth={1.5}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"
            />
            <input
              data-testid="research-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, journal, keyword, year…"
              className="w-full bg-[#F8FAFC] border border-[#E2E8F0] focus:border-[#0A2540] focus:bg-white pl-10 pr-10 py-3 font-sans text-sm text-[#0A2540] placeholder:text-[#94A3B8] outline-none transition-colors"
            />
            {query && (
              <button
                data-testid="research-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0A2540]"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2" data-testid="topic-filters">
            <button
              data-testid="topic-filter-all"
              onClick={() => setActiveTopic(null)}
              className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest-plus border transition-colors ${
                !activeTopic
                  ? "bg-[#0A2540] text-white border-[#0A2540]"
                  : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0A2540]"
              }`}
            >
              All
            </button>
            {topics.map((t) => (
              <button
                key={t}
                data-testid={`topic-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setActiveTopic(t === activeTopic ? null : t)}
                className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest-plus border transition-colors ${
                  activeTopic === t
                    ? "bg-[#B08A4A] text-white border-[#B08A4A]"
                    : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#B08A4A]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div
            data-testid="research-loading"
            className="py-20 text-center font-mono text-xs uppercase tracking-widest-plus text-[#94A3B8]"
          >
            Loading publications…
          </div>
        )}

        {error && (
          <div
            data-testid="research-error"
            className="py-20 text-center font-serif text-lg text-red-700"
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="hidden md:grid grid-cols-12 gap-6 border-b-2 border-[#0A2540] pb-4 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569]">
              <div className="col-span-1">№</div>
              <div className="col-span-5">Study Title</div>
              <div className="col-span-3">Journal & Year</div>
              <div className="col-span-3">Clinical Impact Summary</div>
            </div>

            <ul data-testid="papers-list">
              {filtered.length === 0 && (
                <li className="py-16 text-center font-serif italic text-[#94A3B8]">
                  No publications match your filters.
                </li>
              )}
              {filtered.map((p, idx) => {
                const isOpen = expandedId === p.id;
                return (
                  <li
                    key={p.id}
                    data-testid={`paper-row-${idx}`}
                    className="border-b border-[#E2E8F0]"
                  >
                    <button
                      onClick={() => setExpandedId(isOpen ? null : p.id)}
                      data-testid={`paper-row-toggle-${idx}`}
                      className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 py-6 md:py-7 hover:bg-[#F8FAFC] transition-colors group"
                    >
                      <div className="md:col-span-1 font-mono text-xs text-[#B08A4A] pt-1">
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                      <div className="md:col-span-5">
                        <h3 className="font-serif text-xl md:text-2xl text-[#0A2540] leading-tight group-hover:text-[#2E5AA5] transition-colors">
                          {p.title}
                        </h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {p.topics.slice(0, 4).map((t) => (
                            <span
                              key={t}
                              className="font-mono text-[9px] uppercase tracking-widest-plus text-[#475569] border border-[#E2E8F0] px-2 py-0.5"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="md:col-span-3 pt-1">
                        <div className="font-sans text-sm text-[#0A2540] italic leading-snug">
                          {p.journal}
                        </div>
                        <div className="font-mono text-xs text-[#475569] mt-1">
                          {p.year}
                        </div>
                      </div>
                      <div className="md:col-span-3 pt-1 flex md:justify-between items-start gap-3">
                        <p className="font-sans text-xs text-[#475569] leading-relaxed line-clamp-2 md:pr-4">
                          {p.summary.split(". ")[0]}.
                        </p>
                        <ChevronDown
                          size={16}
                          strokeWidth={1.5}
                          className={`text-[#94A3B8] shrink-0 transition-transform duration-300 mt-0.5 ${
                            isOpen ? "rotate-180 text-[#0A2540]" : ""
                          }`}
                        />
                      </div>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{
                            duration: 0.4,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className="overflow-hidden"
                        >
                          <div
                            data-testid={`paper-row-expanded-${idx}`}
                            className="bg-[#F8FAFC] border-l-2 border-[#B08A4A] px-6 md:px-10 py-8 mb-2 grid grid-cols-1 md:grid-cols-12 gap-6"
                          >
                            <div className="md:col-span-8">
                              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3">
                                Clinical Impact
                              </div>
                              <p className="font-sans text-base text-[#0A2540] leading-[1.85]">
                                {p.summary}
                              </p>
                              {p.authors && (
                                <p className="mt-4 font-sans text-xs text-[#475569] italic">
                                  Authors: {p.authors}
                                </p>
                              )}
                            </div>
                            <div className="md:col-span-4 space-y-4">
                              <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
                                  Journal & Year
                                </div>
                                <div className="font-serif text-lg text-[#0A2540] italic leading-snug">
                                  {p.journal}
                                </div>
                                <div className="font-mono text-xs text-[#475569] mt-1">
                                  {p.year}
                                </div>
                              </div>
                              {p.doi && (
                                <div>
                                  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
                                    DOI
                                  </div>
                                  <div className="font-mono text-xs text-[#475569] break-all">
                                    {p.doi}
                                  </div>
                                </div>
                              )}
                              <a
                                href={p.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`paper-external-link-${idx}`}
                                className="inline-flex items-center gap-2 mt-2 bg-white text-[#0A2540] border border-[#0A2540] hover:bg-[#0A2540] hover:text-white px-5 py-3 text-[10px] tracking-widest-plus uppercase transition-colors"
                              >
                                {p.external_label || "View via PubMed / ResearchGate"}
                                <ExternalLink size={12} strokeWidth={1.5} />
                              </a>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>

            <div className="mt-10 flex justify-between items-center font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
              <span data-testid="papers-count">
                Showing {filtered.length} of {papers.length}
              </span>
              <span>Indexed · PubMed / ResearchGate / DOI</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ResearchTable;
